/**
 * DOCX Import V2 — Integration wrapper for the custom OOXML-to-ProseMirror parser.
 *
 * This module provides a drop-in alternative to the mammoth-based `importDocxToHtml()`.
 * Instead of converting DOCX → HTML → ProseMirror (with Unicode marker hacks),
 * it goes directly: DOCX → ProseMirror JSON.
 *
 * Usage:
 *   import { importDocxV2 } from './docxImportV2';
 *   const result = await importDocxV2(file);
 *   editor.commands.setContent(result.doc);  // Direct JSON — no HTML round-trip
 *
 * Feature flag:
 *   Pass `useCustomParser: true` to `importDocxWithFlag()` to use this parser,
 *   or `false` to fall back to the mammoth pipeline.
 *
 * @see ooxmlParser/PLAN.md for the full roadmap
 * @see docxImport.ts for the mammoth-based pipeline
 */

import { parseDocxV2IR } from "./ooxmlParserV2";
import type {
  PMDoc,
  PMNode,
  OoxmlParseResult,
  ParsedComment,
  ParsedHeaderFooterConfig,
} from "./ooxmlParser";
import type {
  DocxImportResult,
  ImportedComment,
  ImportedTrackChange,
} from "./docxImport";

// Re-export for convenience
export type { PMDoc, OoxmlParseResult };

// ---------------------------------------------------------------------------
// V2 result type — extends the existing result with ProseMirror JSON doc
// ---------------------------------------------------------------------------

/**
 * Result from the V2 custom parser import.
 * Contains the ProseMirror JSON doc directly (no HTML intermediate).
 */
export interface DocxImportResultV2 {
  /** The ProseMirror document JSON — pass directly to editor.setContent() */
  doc: PMDoc;

  /** Parser warnings/messages */
  messages: string[];

  /** Whether the document contained track changes */
  hasTrackChanges: boolean;

  /** Extracted comment metadata (compatible with ImportedComment) */
  comments: ImportedComment[];

  /** Extracted track change metadata (compatible with ImportedTrackChange) */
  trackChanges: ImportedTrackChange[];

  /** Parsed header/footer content from the DOCX */
  headerFooter?: ParsedHeaderFooterConfig;

  /** Parsed page size / orientation / margins from the DOCX */
  pageSetup?: import("./pageSetup").PageSetup;
}

// ---------------------------------------------------------------------------
// Main import function
// ---------------------------------------------------------------------------

/**
 * Import a DOCX file using the custom OOXML-to-ProseMirror parser.
 *
 * Returns ProseMirror JSON directly — no mammoth, no HTML, no DOM round-trips.
 *
 * @param file - The DOCX file (File, ArrayBuffer, Blob, or Uint8Array)
 * @param options - Optional configuration
 * @returns V2 result with ProseMirror JSON doc and metadata
 */
export async function importDocxV2(
  file: File | ArrayBuffer | Blob | Uint8Array,
  options?: ImportDocxV2Options
): Promise<DocxImportResultV2> {
  // Convert File to ArrayBuffer if needed
  let input: ArrayBuffer | Blob | Uint8Array;
  if (file instanceof File) {
    input = await file.arrayBuffer();
  } else {
    input = file;
  }

  // Parse with the V2 OOXML parser (IR-based pipeline)
  const result = await parseDocxV2IR(input, {
    generateNodeId: options?.generateNodeId,
  });

  // Map ParsedComment → ImportedComment (preserving nested replies)
  const comments: ImportedComment[] = result.comments.map(mapParsedComment);

  // Map ParsedTrackChange → ImportedTrackChange
  const trackChanges: ImportedTrackChange[] = result.trackChanges.map(
    (tc, index) => ({
      id: tc.id,
      type: tc.type,
      author: tc.author,
      date: tc.date,
      text: tc.text,
      // Position is intrinsic in the JSON marks — use index as placeholder
      // for panel display compatibility
      position: index,
    })
  );

  return {
    doc: result.doc,
    messages: result.messages,
    hasTrackChanges: result.hasTrackChanges,
    comments,
    trackChanges,
    headerFooter: result.headerFooter,
    pageSetup: result.pageSetup,
  };
}

// ---------------------------------------------------------------------------
// Feature-flagged import — switch between mammoth and custom parser
// ---------------------------------------------------------------------------

/**
 * Import a DOCX file with a feature flag to choose between mammoth and custom parser.
 *
 * When `useCustomParser` is true:
 *   - Returns a DocxImportResultV2 (with `doc` as ProseMirror JSON)
 *   - Use `editor.commands.setContent(result.doc)` to load content
 *
 * When `useCustomParser` is false (default):
 *   - Falls back to the existing mammoth pipeline (`importDocxToHtml`)
 *   - Returns a DocxImportResult (with `html` as HTML string)
 *   - Use `editor.commands.setContent(result.html)` to load content
 *
 * @param file - The DOCX file
 * @param options - Import options including the feature flag
 */
export async function importDocxWithFlag(
  file: File,
  options?: ImportDocxWithFlagOptions
): Promise<DocxImportResultV2 | DocxImportResult> {
  if (options?.useCustomParser) {
    return importDocxV2(file, options);
  }

  // Fall back to mammoth pipeline
  const { importDocxToHtml } = await import("./docxImport");
  return importDocxToHtml(file, options);
}

/**
 * Type guard: check if a result is from the V2 custom parser.
 * V2 results have a `doc` field (ProseMirror JSON); mammoth results have `html`.
 */
export function isV2Result(
  result: DocxImportResultV2 | DocxImportResult
): result is DocxImportResultV2 {
  return "doc" in result && !("html" in result);
}

// ---------------------------------------------------------------------------
// Options
// ---------------------------------------------------------------------------

export interface ImportDocxV2Options {
  /** Custom node ID generator (e.g., from UniqueIdExtension). */
  generateNodeId?: () => string;
}

export interface ImportDocxWithFlagOptions extends ImportDocxV2Options {
  /** Use the custom OOXML parser instead of mammoth. Default: false */
  useCustomParser?: boolean;
  /** Defer comment marker conversion (mammoth path only). */
  deferCommentConversion?: boolean;
}

// ---------------------------------------------------------------------------
// GCS image upload for V2 path
// ---------------------------------------------------------------------------

/**
 * Walk a ProseMirror JSON document tree and upload all base64 images to GCS.
 *
 * Replaces each image node's `src` (base64 data URL) with a signed GCS URL
 * and adds a `gcsFileName` attr for URL refresh. Mirrors the V1
 * `uploadDocxImagesToGCS()` behavior but operates on JSON instead of HTML DOM.
 *
 * Mutates the doc in place for efficiency (avoids deep-cloning large docs).
 *
 * @param doc - The ProseMirror document JSON (mutated in place)
 * @param docId - Document ID for GCS path
 * @param backendUrl - Backend base URL
 * @param workproductId - Workproduct ID for GCS path
 * @returns Number of images successfully uploaded
 */
export async function uploadDocxImagesToGCSFromJson(
  doc: PMDoc,
  docId: string,
  backendUrl: string,
  workproductId: string
): Promise<number> {
  // Collect all image nodes with base64 sources
  const imageNodes: PMNode[] = [];
  collectImageNodes(doc.content, imageNodes);

  if (imageNodes.length === 0) return 0;

  let uploaded = 0;

  for (let i = 0; i < imageNodes.length; i++) {
    const node = imageNodes[i];
    const src = node.attrs?.src as string;
    if (!src || typeof src !== "string" || !src.startsWith("data:image/"))
      continue;

    // Parse data URI: data:image/png;base64,XXXX...
    const match = src.match(/^data:(image\/[a-z+]+);base64,(.+)$/i);
    if (!match) continue;

    const contentType = match[1];
    const base64 = match[2];

    try {
      const resp = await fetch(
        `${backendUrl}/api/document-agent/upload-docx-image`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
          body: JSON.stringify({
            base64,
            contentType,
            docId,
            workproductId,
            index: i,
          }),
        }
      );

      if (!resp.ok) {
        continue;
      }

      const { gcsFileName, signedUrl } = await resp.json();

      // Replace base64 src with signed URL and store GCS reference
      if (!node.attrs) node.attrs = {};
      node.attrs.src = signedUrl;
      node.attrs.gcsFileName = gcsFileName;
      uploaded++;
    } catch (err) {}
  }

  return uploaded;
}

/**
 * Recursively collect all image nodes from a ProseMirror node tree.
 */
function collectImageNodes(
  nodes: PMNode[] | undefined,
  result: PMNode[]
): void {
  if (!nodes) return;
  for (const node of nodes) {
    if (node.type === "image") {
      result.push(node);
    }
    // Recurse into content (paragraphs, table cells, list items, etc.)
    if (node.content) {
      collectImageNodes(node.content, result);
    }
  }
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/** Map a ParsedComment to ImportedComment (flat list with parentCommentId). */
function mapParsedComment(c: ParsedComment): ImportedComment {
  const mapped: ImportedComment = {
    id: c.id,
    author: c.author,
    date: c.date,
    content: c.content,
    commentedText: c.commentedText,
  };
  if (c.parentCommentId) {
    mapped.parentCommentId = c.parentCommentId;
  }
  return mapped;
}
